import dayjs from 'dayjs';
import { isEqual } from 'lodash-es';
import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { Theme } from '@emotion/react';
import { clone, ConfigModel, removeUndefinedProps } from '@ibe/services';
import { ApiGeoUnit, ApiItemType, ApiRoomContainer } from '@ibe/api';
import PackageSearchParams from './PackageSearchParams';
import PackageParams from '../PackageDetails/PackageParams';
import DateInputType from '../../../Models/DateInputType';
import DestinationItem from '../../../Models/DestinationItem';
const ADD_DAYS_TO_START = 7;
const ADD_MONTHS_TO_END = 2;
const ADD_DAYS_TO_END = 14;

class PackageSearchStore {
  error: string | null = null;

  isReady = false;

  partialSearchParams: Partial<PackageSearchParams> = {};

  theme?: Theme;

  _validationErrorMessages?: string[] = [];

  _showValidationErrorMessage = false;

  _destinationVisibilityMap: Map<string, boolean> = new Map<string, boolean>();

  ignoreDefaults?: boolean;

  _areDestinationsInTheSameCountry = false;

  constructor(
    public config: ConfigModel,
    theme?: Theme,
    initialSearchParams?: Partial<PackageSearchParams>,
    ignoreDefaults?: boolean
  ) {
    makeObservable(this, {
      isReady: observable,
      error: observable,
      partialSearchParams: observable,
      _showValidationErrorMessage: observable,
      _validationErrorMessages: observable,
      _areDestinationsInTheSameCountry: observable,

      searchParams: computed,
      startDate: computed,
      fullStartDate: computed,
      fullEndDate: computed,
      origin: computed,
      origins: computed,
      destination: computed,
      destinations: computed,
      destinationItem: computed,
      packageServiceGroup: computed,
      mainServiceCodes: computed,
      duration: computed,
      hasValidationErrors: computed,

      setDestination: action,
      setDestinations: action,
      setDestinationItem: action,
      setMainServiceCodes: action,
      setOrigin: action,
      setOrigins: action,
      setDate: action,
      setOccupancy: action,
      setParams: action,
      setStartDate: action,
      setEndDate: action,
      setDuration: action,
      setValidationErrorMessages: action,
      setShowValidationErrorMessage: action,
      setAreDestinationsInTheSameCountry: action
    });
    this.theme = theme;
    this.config = config;
    this.ignoreDefaults = ignoreDefaults;
    if (!!initialSearchParams) {
      this.setParams(initialSearchParams);
    }
  }

  defaultSearchParams(subType: ApiItemType, dateInputType: DateInputType): PackageSearchParams {
    return {
      origin: undefined,
      origins: [],
      destination: undefined,
      destinations: [],
      duration: undefined,
      startDate: !this.ignoreDefaults
        ? subType === ApiItemType.PREDEFINEDPACKAGE && dateInputType !== DateInputType.FULL
          ? dayjs().startOf('month').format(this.config.formatDate)
          : dayjs()
              .add(
                this.theme?.dateRangePickerOptions?.defaultDaysAddedToStart || ADD_DAYS_TO_START,
                'day'
              )
              .format(this.config.formatDate)
        : undefined,
      endDate: !this.ignoreDefaults
        ? subType === ApiItemType.PREDEFINEDPACKAGE && dateInputType !== DateInputType.FULL
          ? dayjs().add(ADD_MONTHS_TO_END, 'month').startOf('month').format(this.config.formatDate)
          : dayjs()
              .add(
                this.theme?.dateRangePickerOptions?.defaultDaysAddedToEnd || ADD_DAYS_TO_END,
                'day'
              )
              .format(this.config.formatDate)
        : undefined,
      occupancy: [
        {
          adults: 2,
          children: 0,
          infants: 0,
          childrenAges: [],
          infantsAges: []
        }
      ],
      packageServiceGroup: '',
      dateInputType: dateInputType,
      goToDetails: true
    };
  }

  initDestinationVisibilityMap() {
    this.searchParams.destinations?.forEach(value => {
      if (value.typeCode === 'COUNTRY') {
        this._destinationVisibilityMap.set(value.id, true);
      } else {
        this._destinationVisibilityMap.set(value.parentId || 'undefined', true);
      }
    });
    console.log('init', this._destinationVisibilityMap);
  }

  get searchParams(): PackageSearchParams {
    return this.fillWithDefaults(this.partialSearchParams);
  }

  get origin(): ApiGeoUnit | undefined {
    return this.searchParams.origin;
  }

  get origins(): ApiGeoUnit[] | undefined {
    return this.searchParams.origins;
  }

  get destination(): ApiGeoUnit | undefined {
    return this.searchParams.destination;
  }

  get destinations(): DestinationItem[] | undefined {
    return this.searchParams.destinations;
  }

  get destinationVisibilityMap(): Map<string, boolean> {
    return this._destinationVisibilityMap;
  }

  get destinationItem(): DestinationItem | undefined {
    return this.searchParams.destinationItem;
  }

  get duration(): number | undefined {
    return this.searchParams.duration;
  }

  get mainServiceCodes(): string[] | undefined {
    return this.searchParams.mainServiceCodes;
  }

  get startDate(): Date {
    const date = dayjs(this.searchParams.startDate);
    return date.startOf('month').toDate();
  }

  get fullStartDate(): string {
    return this.searchParams.startDate || '';
  }

  get fullEndDate(): string {
    return this.searchParams.endDate || '';
  }

  get packageServiceGroup(): string {
    return this.searchParams.packageServiceGroup || '';
  }

  get hasValidationErrors(): boolean {
    return !!this._validationErrorMessages?.length;
  }

  updateDestinationVisibilityMap(key: string, value: boolean): void {
    this._destinationVisibilityMap.set(key, value);
  }

  setOrigin(origin: ApiGeoUnit | undefined): void {
    this.setParams({ origin });
  }

  setDuration(duration: number | undefined): void {
    if (!duration) {
      const params = clone(toJS(this.partialSearchParams)) as Partial<PackageSearchParams>;
      delete params.duration;
      this.partialSearchParams = params;
    }
    this.setParams({ duration });
  }

  setOrigins(origins: ApiGeoUnit[]): void {
    this.setParams({ origins });
  }

  setDestination(destination: ApiGeoUnit | undefined): void {
    this.setParams({ destination });
  }

  setDestinations(destinations: DestinationItem[] | undefined): void {
    this.setParams({ destinations });
  }

  setDestinationItem(destinationItem: DestinationItem | undefined): void {
    this.setParams({ destinationItem });
  }

  setMainServiceCodes(mainServiceCodes: string[]): void {
    this.setParams({ mainServiceCodes });
  }

  setDate(date: Date | null): void {
    const params = clone(toJS(this.partialSearchParams)) as Partial<PackageSearchParams>;

    params.startDate = undefined;
    params.endDate = undefined;
    if (date) {
      const d = dayjs(date);
      params.startDate = d.startOf('month').format(this.config.formatDate);
      const addMonthToEnd = 1;
      params.endDate = d
        .startOf('month')
        .add(addMonthToEnd, 'month')
        .format(this.config.formatDate);
    }

    this.partialSearchParams = params;
  }

  setStartDate(date: string | undefined): void {
    this.setParams({
      startDate: !!date ? dayjs(date).format(this.config.formatDate) : undefined
    });
  }

  setEndDate(date: string | null): void {
    this.setParams({
      endDate: !!date ? dayjs(date).format(this.config.formatDate) : undefined
    });
  }

  setOccupancy(o: ApiRoomContainer[]): void {
    this.setParams({ occupancy: o || undefined });
  }

  setPackageServiceGroup(o: string): void {
    this.setParams({ packageServiceGroup: o || '' });
  }

  setParams(p: Partial<PackageSearchParams>): void {
    if (!isEqual(toJS(this.partialSearchParams), p)) {
      this.partialSearchParams = {
        ...(clone(toJS(this.partialSearchParams || {})) as Partial<PackageSearchParams>),
        ...removeUndefinedProps<PackageSearchParams>(p || {})
      };
    }
    this.validateParamsAndShowError();
  }

  private fillWithDefaults(values: Partial<PackageSearchParams>): PackageSearchParams {
    const params = {
      ...(clone(
        toJS(
          this.defaultSearchParams(
            values.subType || ApiItemType.PREDEFINEDPACKAGE,
            values.dateInputType || DateInputType.MONTH
          )
        ) || {}
      ) as Partial<PackageSearchParams>),
      ...removeUndefinedProps<PackageSearchParams>(values || {})
    };

    if (params.subType === ApiItemType.PREDEFINEDPACKAGE) {
      if (params.startDate && !params.endDate) {
        const addMonthToEnd = 1;
        params.endDate = dayjs(params.startDate, this.config.formatDate)
          .add(addMonthToEnd, 'month')
          .format(this.config.formatDate);
      }
      if (params.dateInputType !== DateInputType.FULL) {
        // always set dates to first day of month:
        params.startDate = dayjs(params.startDate, this.config.formatDate)
          .startOf('month')
          .format(this.config.formatDate);

        params.endDate = dayjs(params.endDate, this.config.formatDate)
          .startOf('month')
          .format(this.config.formatDate);
      }
    }
    return params;
  }

  resetDetail(): void {
    (this.searchParams as PackageParams).packageDetailId = undefined;
    (this.searchParams as PackageParams).itemId = undefined;
  }

  setValidationErrorMessages(res: string[]) {
    this._validationErrorMessages = res;
  }

  get validationErrorMessages(): string[] | undefined {
    return this._validationErrorMessages;
  }

  setShowValidationErrorMessage(res: boolean) {
    this._showValidationErrorMessage = res;
  }

  get showValidationErrorMessage(): boolean {
    return this._showValidationErrorMessage;
  }

  setAreDestinationsInTheSameCountry(res: boolean) {
    this._areDestinationsInTheSameCountry = res;
  }

  get areDestinationsInTheSameCountry(): boolean {
    return this._areDestinationsInTheSameCountry;
  }

  public validateParamsAndShowError(): void {}
}

export default PackageSearchStore;
